import * as joint from '@clientio/rappid';
import { generateCustomObjects } from '../shapes/custom-objects'
import { toast } from 'react-toastify';
import FlowireAPI from '../../../flowireapi/FlowireAPI';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Quill } from 'react-quill';
import { streamTableExport } from './stream-to-excel.js';
import resetEconVars from './setEconVars.js';
import simulateEconVars from './simulateEcon.js';
import { multivariateAnalysis } from './multivariateAnalysis.js';
import calculateOpexCapex from './calculateCapexOpexService.js';
import calculateFeedEP from './calculateFeedEPService.js';
import { reactions_Left } from './specReactionService.js';
import { spec_Reactions } from './specReactionService.js';
import TextOverlayManager from './TextOverlayManager.js';

class KitchenSinkService {

    constructor(el, toolbarService, keyboardService, simulatorService, openInfoStreamPropertyPopup, openFinancialStreamPropertyPopup, tempCloseAllPopups, tempReopenAllPopups, closeAllPopups, handleOpenWizard, isAuthenticated, getIdTokenClaims, navigate, toggleSaveNamePopup, useDesigner, setGraphData, openConfirmPopup, hasUpdated, isUpdated, setMAGraphData, setMAGraphLoading, graphGroup, isTemplate) {
        this.hasUpdated = hasUpdated
        this.isUpdated = isUpdated
        this.getIdTokenClaims = getIdTokenClaims
        this.setGraphData = setGraphData
        this.openConfirmPopup = openConfirmPopup
        this.isAuthenticated = isAuthenticated
        this.navigate = navigate
        this.toggleSaveNamePopup = toggleSaveNamePopup
        this.economicsSimulated = false

        // backwards compatibility for older shapes
        this.exportStylesheet = '.scalable * { vector-effect: non-scaling-stroke }';
        this.el = el;

        // apply current joint js theme
        const view = new joint.mvc.View({ el });
        view.delegateEvents({
            'mouseup input[type="range"]': (evt) => evt.target.blur()
        });

        this.toolbarService = toolbarService;
        //this.inspectorService = inspectorService;
        this.keyboardService = keyboardService;
        this.simulatorService = simulatorService;
        this.openInfoStreamPropertyPopup = openInfoStreamPropertyPopup;
        this.openFinancialStreamPropertyPopup = openFinancialStreamPropertyPopup;
        // this.toggleOverallFinancialStreamPropertyPopup = toggleOverallFinancialStreamPropertyPopup;
        this.overallPopupOpen = false;
        this.simulatorService.globalObjectsList = this.econVars
        this.simulatedOnce = false;
        this.tempCloseAllPopups = tempCloseAllPopups
        this.tempReopenAllPopups = tempReopenAllPopups
        this.closeAllPopups = closeAllPopups
        this.simulateOnLoad = false
        this.handleOpenWizard = handleOpenWizard
        this.hitSaveOnce = false;
        this.useDesigner = useDesigner

        this.selectMousePoint = {
            x: 0,
            y: 0
        };

        this.uuid = null;
        this.graphName = null;
        this.setMAGraphData = setMAGraphData
        this.setMAGraphLoading = setMAGraphLoading
        this.graphGroup = graphGroup
        this.isTemplate = isTemplate

        this.calculateOpexCapex = calculateOpexCapex.bind(this);
        this.simulateEconVars = simulateEconVars.bind(this);
        this.resetEconVars = resetEconVars.bind(this);
        this.resetEconVars();
        this.calculateFeedEP = calculateFeedEP.bind(this);
        this.spec_Reactions = spec_Reactions.bind(this);

    }

// Helper function to check if Quill is properly initialized
    isQuillProperlyInitialized(elementId) {
        const element = document.getElementById(elementId);
        if (!element) return false;
        
        // Check if it has the proper Quill structure
        const hasQuillEditor = element.querySelector('.ql-editor');
        const hasQuillClipboard = element.querySelector('.ql-clipboard');
        const hasQuillTooltip = element.querySelector('.ql-tooltip');
        
        // A properly initialized Quill should have these elements
        return hasQuillEditor && hasQuillClipboard && hasQuillTooltip;
    }

    // Helper function to check if Quill is broken (raw contenteditable)
    isQuillBroken(elementId) {
        const element = document.getElementById(elementId);
        if (!element) return true;
        
        // Check if it's just a raw contenteditable div (broken state)
        const isContentEditable = element.hasAttribute('contenteditable') || element.hasAttribute('content-editable');
        const hasQuillStructure = element.querySelector('.ql-editor');
        const hasInnerHtml = element.hasAttribute('inner-h-t-m-l'); // This is a sign it's broken
        
        // If it's contenteditable but has no Quill structure, it's broken
        // OR if it has the inner-h-t-m-l attribute, it's definitely broken
        return (isContentEditable && !hasQuillStructure) || hasInnerHtml;
    }

    // Helper function to apply styles to elements
    applyStyles(shape) {
        const elementId = shape.attr('shapename/id');
        const element = document.getElementById(elementId);
        
        if (element) {
            if (shape.attr('shapename/style/top')) {
                element.style.top = shape.attr('shapename/style/top');
            }
            if (shape.attr('shapename/style/left')) {
                element.style.left = shape.attr('shapename/style/left');
            }
            if (shape.attr('shapename/style/width')) {
                element.style.width = shape.attr('shapename/style/width');
            }
            if (shape.attr('shapename/style/height')) {
                element.style.height = shape.attr('shapename/style/height');
            }
        }
    }

    // Create a function to initialize Quill when element is ready
    initializeQuillWhenReady(shape) {
        const elementId = shape.attr('shapename/id');
    
        
        // First check if Quill is already properly initialized
        if (this.isQuillProperlyInitialized(elementId)) {
            this.applyStyles(shape);
            return;
        }
        
        const observer = new MutationObserver((mutations, obs) => {
            const element = document.getElementById(elementId);    

            
            if (element && element.offsetParent !== null) {
                const isBroken = this.isQuillBroken(elementId);
                const isProperlyInit = this.isQuillProperlyInitialized(elementId);
                
                
                if (isBroken || !isProperlyInit) {
                    
                    try {
                        // Clean up any existing Quill instance
                        if (element.__quill) {
                            element.__quill = null;
                        }
                        
                        // Get saved content from multiple possible sources
                        const savedContent = shape.attr('shapename/innerHTML') || 
                                           element.getAttribute('inner-h-t-m-l') || 
                                           '<p><br></p>';
                        
                        
                        // Clear element
                        element.innerHTML = '';
                        
                        // Initialize Quill first
                        const quill = new Quill(element, {
                            modules: {},
                            theme: 'bubble',
                            bounds: document.getElementById(shape.id + 'QuillParentDiv')
                        });
                        
                        // Now set the content using Quill's API
                        if (savedContent && savedContent !== '<p><br></p>') {
                            quill.clipboard.dangerouslyPasteHTML(savedContent);
                        }
                        
                        // Apply saved styles
                        this.applyStyles(shape);
                        
                        // Add process-shape class to editor
                        const editor = element.querySelector('.ql-editor');
                        if (editor) {
                            editor.classList.add("process-shape");
                        }
                        
                        // Ensure the parent div is visible
                        const parentDiv = document.getElementById(shape.id + 'QuillParentDiv');
                        if (parentDiv) {
                            parentDiv.style.display = shape.attr('foreignObject/style/display') || 'block';
                        }
                        
                        
                    } catch (error) {
                        console.error('Failed to initialize Quill for element:', elementId, error);
                    }
                } else {
                    this.applyStyles(shape);
                }
                
                obs.disconnect();
            }
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'contenteditable', 'inner-h-t-m-l'] // Watch for more attributes
        });
        
        setTimeout(() => {
            observer.disconnect();
        }, 2000);
    }

    startRappid() {
        this.initializePaper();
        this.initializeSelection();
        this.initializeToolsAndInspector();
        this.initializeToolbar();
        this.initializeKeyboardShortcuts();
    }

    stopRappid() {
        this.paper.remove();
        this.paperScroller.remove();
        this.selection.remove();
        this.toolbarService.toolbar.remove();
        this.keyboardService.keyboard.stopListening();
    }

    initializePaper() {
        const graph = this.graph = new joint.dia.Graph({}, { cellNamespace: joint.shapes });

        this.commandManager = new joint.dia.CommandManager({ graph: graph });

        const paper = this.paper = new joint.dia.Paper({
            width: 1587,
            height: 1123,
            gridSize: 10,
            drawGrid: true,
            model: graph,
            async: true,
            snapLinks: { radius: 70 },
            defaultConnectionPoint: { name: 'anchor' },
            namespace: joint.shapes,
            interactive: false,
        });

        generateCustomObjects(joint, graph, paper, this.simulatorService)

        this.snaplines = new joint.ui.Snaplines({ paper: paper });

        const paperScroller = this.paperScroller = new joint.ui.PaperScroller({
            paper,
            autoResizePaper: false,
            scrollWhileDragging: true,
            cursor: 'grab'
        });
        this.renderPlugin('.paper-container', paperScroller);
        paperScroller.render().center();
        paperScroller.zoomToRect({ x: 0, y: 0, width: 1587, height: 1123 })
        this.baseline_Zoom = paperScroller.zoom();

        // Initialize text overlay manager for detached Quill editors (read-only in Simulator)
        this.textOverlayManager = new TextOverlayManager(paper, paperScroller, graph, { readOnly: true });

        paper.on('cell:pointerdblclick', (cellView) => {
            var label = cellView.$('text');
            label.attr('contenteditable', 'true');
            label.focus();
        });

        paper.on('blank:pointerdown', paperScroller.startPanning);

        paper.on('blank:pointerdown', () => {
            paper.removeTools();
        });

        paper.on('render:done', () => {
            const inputs = document.querySelectorAll('input[joint-selector="labelInputDiv"]');

            inputs.forEach((input) => {
                input.disabled = true;
            });
        });

        paper.on('blank:pointerdown', () => {
            paper.removeTools();
            document.activeElement.blur();
            window.getSelection().empty()
        });

        paper.on('paper:pan', (evt, tx, ty) => {
            evt.preventDefault();
            paperScroller.el.scrollLeft += tx;
            paperScroller.el.scrollTop += ty;
        });

        paper.on('paper:pinch', (evt, ox, oy, scale) => {
            // the default is already prevented
            const zoom = paperScroller.zoom();
            paperScroller.zoom(zoom * scale, { min: 0.2, max: 5, ox, oy, absolute: true });
            // Update text overlay positions after zoom
            if (this.textOverlayManager) this.textOverlayManager.onZoom();
        });

        paper.on('blank:mousewheel', (evt, x, y, delta) => {
            evt.preventDefault();
            if (delta > 0) {
                paperScroller.zoom(0.1, { max: 1.4, grid: 0.1 });
                paperScroller.setCursor('zoom-in')
            }
            else {
                paperScroller.zoom(-0.1, { min: this.baseline_Zoom, grid: 0.1 });
                paperScroller.setCursor('zoom-out')
                if (paperScroller.zoom() <= this.baseline_Zoom) {
                    //paperScroller.center();
                    paperScroller.zoomToRect({ x: 0, y: -100, width: 1587, height: 1123 });
                }
            }
            // Update text overlay positions after zoom
            if (this.textOverlayManager) this.textOverlayManager.onZoom();
        });

        paper.on('cell:mousewheel', (cellView, evt, x, y, delta) => {
            evt.preventDefault();
            if (delta > 0) {
                paperScroller.zoom(0.1, { max: 1.4, grid: 0.1 });
                paperScroller.setCursor('zoom-in')

            }
            else {
                paperScroller.zoom(-0.1, { min: this.baseline_Zoom, grid: 0.1 });
                paperScroller.setCursor('zoom-out')
                if (paperScroller.zoom() <= this.baseline_Zoom) {
                    //paperScroller.center();
                    paperScroller.zoomToRect({ x: 0, y: -100, width: 1587, height: 1300 })
                }
            }
            // Update text overlay positions after zoom
            if (this.textOverlayManager) this.textOverlayManager.onZoom();
        });
    }

    initializeSelection() {

        this.clipboard = new joint.ui.Clipboard();
        this.selection = new joint.ui.Selection({
            paper: this.paper,
            useModelGeometry: true,
            translateConnectedLinks: joint.ui.Selection.ConnectedLinksTranslation.SUBGRAPH
        });
        this.selection.collection.on('reset add remove', this.onSelectionChange.bind(this));

        const keyboard = this.keyboardService.keyboard;

        // Initiate selecting when the user grabs the blank area of the paper while the Shift key is pressed.
        // Otherwise, initiate paper pan.
        this.paper.on('blank:pointerdown', (evt, x, y) => {
            if (keyboard.isActive('shift', evt)) {
                this.selection.startSelecting(evt);
            }
            else {
                this.selection.collection.reset([]);
                this.paperScroller.startPanning(evt);
                this.paper.removeTools();
            }
        });

        this.paper.on('element:pointerdown', (elementView, evt) => {
            // Select an element if CTRL/Meta key is pressed while the element is clicked.
            if (keyboard.isActive('ctrl meta', evt)) {
                this.selection.collection.add(elementView.model);
            }

        });

        this.selection.on('selection-box:pointerdown', (elementView, evt) => {
            // Unselect an element if the CTRL/Meta key is pressed while a selected element is clicked.
            if (keyboard.isActive('ctrl meta', evt)) {
                this.selection.collection.remove(elementView.model);
            }

        }, this);
    }

    onSelectionChange() {
        const { paper, selection } = this;
        const { collection } = selection;
        paper.removeTools();
        joint.ui.Halo.clear(paper);
        joint.ui.FreeTransform.clear(paper);
        joint.ui.Inspector.close();
        if (collection.length === 1) {
            const primaryCell = collection.first();
            const primaryCellView = paper.findViewByModel(primaryCell);
            selection.destroySelectionBox(primaryCell);
            this.selectPrimaryCell(primaryCellView);
        }
        else if (collection.length === 2) {
            collection.each(function (cell) {
                selection.createSelectionBox(cell);
            });
        }
    }

    selectPrimaryCell(cellView) {
        const cell = cellView.model;
        if (cell.isElement()) {
            this.selectPrimaryElement(cellView);
        }
        else {
            this.selectPrimaryLink(cellView);
        }
    }

    selectPrimaryElement(elementView) {
        let r = false
        if (elementView.model.get('reaction')) {
            if (!elementView.model.get('rx_spec')) {
                r = true
            }
        }

        var handles = [{
            name: 'myCustomAction',
            position: 'ne',
            icon: 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPCFET0NUWVBFIHN2ZyBQVUJMSUMgIi0vL1czQy8vRFREIFNWRyAxLjEvL0VOIiAiaHR0cDovL3d3dy53My5vcmcvR3JhcGhpY3MvU1ZHLzEuMS9EVEQvc3ZnMTEuZHRkIj4KPHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2ZXJzaW9uPSIxLjEiIHdpZHRoPSIyNjVweCIgaGVpZ2h0PSIyNjVweCIgdmlld0JveD0iLTAuNSAtMC41IDI2NSAyNjUiPjxkZWZzLz48Zz48ZWxsaXBzZSBjeD0iMTMyIiBjeT0iMTMyIiByeD0iMTI1IiByeT0iMTI1IiBmaWxsPSJyZ2IoMjU1LCAyNTUsIDI1NSkiIHN0cm9rZT0iIzY5NmI4YSIgc3Ryb2tlLXdpZHRoPSIxNSIgcG9pbnRlci1ldmVudHM9ImFsbCIvPjxnIHRyYW5zZm9ybT0idHJhbnNsYXRlKC0wLjUgLTAuNSkiPjxzd2l0Y2g+PGZvcmVpZ25PYmplY3QgcG9pbnRlci1ldmVudHM9Im5vbmUiIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIHJlcXVpcmVkRmVhdHVyZXM9Imh0dHA6Ly93d3cudzMub3JnL1RSL1NWRzExL2ZlYXR1cmUjRXh0ZW5zaWJpbGl0eSIgc3R5bGU9Im92ZXJmbG93OiB2aXNpYmxlOyB0ZXh0LWFsaWduOiBsZWZ0OyI+PGRpdiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94aHRtbCIgc3R5bGU9ImRpc3BsYXk6IGZsZXg7IGFsaWduLWl0ZW1zOiB1bnNhZmUgY2VudGVyOyBqdXN0aWZ5LWNvbnRlbnQ6IHVuc2FmZSBjZW50ZXI7IHdpZHRoOiAyNDhweDsgaGVpZ2h0OiAxcHg7IHBhZGRpbmctdG9wOiAxMzJweDsgbWFyZ2luLWxlZnQ6IDhweDsiPjxkaXYgZGF0YS1kcmF3aW8tY29sb3JzPSJjb2xvcjogcmdiKDAsIDAsIDApOyAiIHN0eWxlPSJib3gtc2l6aW5nOiBib3JkZXItYm94OyBmb250LXNpemU6IDBweDsgdGV4dC1hbGlnbjogY2VudGVyOyI+PGRpdiBzdHlsZT0iZGlzcGxheTogaW5saW5lLWJsb2NrOyBmb250LXNpemU6IDEycHg7IGZvbnQtZmFtaWx5OiBIZWx2ZXRpY2E7IGNvbG9yOiByZ2IoMCwgMCwgMCk7IGxpbmUtaGVpZ2h0OiAxLjI7IHBvaW50ZXItZXZlbnRzOiBhbGw7IHdoaXRlLXNwYWNlOiBub3JtYWw7IG92ZXJmbG93LXdyYXA6IG5vcm1hbDsiPjxiPjxmb250IGZhY2U9Ikdlb3JnaWEiIHN0eWxlPSJmb250LXNpemU6IDE1M3B4IiBjb2xvcj0iIzY5NmI4YSI+aTwvZm9udD48L2I+PC9kaXY+PC9kaXY+PC9kaXY+PC9mb3JlaWduT2JqZWN0Pjx0ZXh0IHg9IjEzMiIgeT0iMTM2IiBmaWxsPSJyZ2IoMCwgMCwgMCkiIGZvbnQtZmFtaWx5PSJIZWx2ZXRpY2EiIGZvbnQtc2l6ZT0iMTJweCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+aTwvdGV4dD48L3N3aXRjaD48L2c+PC9nPjxzd2l0Y2g+PGcgcmVxdWlyZWRGZWF0dXJlcz0iaHR0cDovL3d3dy53My5vcmcvVFIvU1ZHMTEvZmVhdHVyZSNFeHRlbnNpYmlsaXR5Ii8+PGEgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMCwtNSkiIHhsaW5rOmhyZWY9Imh0dHBzOi8vd3d3LmRpYWdyYW1zLm5ldC9kb2MvZmFxL3N2Zy1leHBvcnQtdGV4dC1wcm9ibGVtcyIgdGFyZ2V0PSJfYmxhbmsiPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtc2l6ZT0iMTBweCIgeD0iNTAlIiB5PSIxMDAlIj5UZXh0IGlzIG5vdCBTVkcgLSBjYW5ub3QgZGlzcGxheTwvdGV4dD48L2E+PC9zd2l0Y2g+PC9zdmc+'
        }];

        if (r) {
            handles.push({
                name: 'myCustomAction2',
                position: 'nw',
                icon: 'data:image/svg+xml;base64,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'
            })
        }
        var halo = new joint.ui.Halo({
            cellView: elementView,
            handles: handles,
            useModelGeometry: true,
            boxContent: false,
            model: this.graph,
        }).render();

        var graph = this.graph
        var paper = this.paper

        halo.on('action:myCustomAction:pointerdown', (evt) => {
            if (elementView.model.prop('unitType') == 'pipework') {
                return
            }
            //alert('My Control Button Clicked!');
            this.hasUpdated(true)
            this.openFinancialStreamPropertyPopup(graph, elementView.model.id, this.econVars, evt.pageX, evt.pageY, this.orgSectors)
        });

        halo.on('action:myCustomAction2:pointerdown', (evt) => {
            //alert('My Control Button Clicked!');

            this.hasUpdated(true)
            this.spec_Reactions([elementView.model])
        });

    }

    selectPrimaryLink(linkView) {
        if (linkView.model.attributes.type.toLowerCase() === 'link') return;

        var graph = this.graph
        var paper = this.paper
        var openInfoStreamPropertyPopup = this.openInfoStreamPropertyPopup
        const pathElement = linkView.el.querySelector('path');
        var midPoint;
        if (pathElement && typeof pathElement.getTotalLength === 'function') {
            const pathLength = pathElement.getTotalLength();
            midPoint = pathElement.getPointAtLength(pathLength / 2);
        }
        const sourcePoint = midPoint;

        var InfoButton = joint.linkTools.Button.extend({
            name: 'info-button',
            options: {
                focusOpacity: 0.5,
                distance: 60,
                offset: 0,
                action: function (evt) {
                    if (this.model.prop('CustomName') != '') { openInfoStreamPropertyPopup(graph, this.model, evt.pageX, evt.pageY); }
                    else {
                        alert('To add properties to this segment, please provide a unique label (double click).\nAlternatively, you could find a segment of this stream that already has a label, and can add properties there.')
                    }
                },
                markup: [{
                    tagName: 'circle',
                    selector: 'button',
                    attributes: {
                        'r': 10,
                        'fill': '#ffffff',
                        'cursor': 'pointer',
                        'stroke': '#696B8A'
                    }
                }, {
                    tagName: 'path',
                    selector: 'icon',
                    attributes: {
                        'd': 'M -2 4 2 4 M 0 3 0 0 M -2 -1 1 -1 M -1 -4 1 -4',
                        'fill': 'none',
                        'stroke': '#696B8A',
                        'stroke-width': 2,
                        'pointer-events': 'none'
                    }
                }]
            }
        });

        let mouse_Entry_Point = { x: this.selectMousePoint.x, y: this.selectMousePoint.y }
        let paper_Entry_Point = this.paper.pageToLocalPoint(mouse_Entry_Point)
        let distance_On_Link_To_Entry_Point = linkView.getClosestPointLength(paper_Entry_Point)

        const ns = joint.linkTools;
        const toolsView = new joint.dia.ToolsView({
            name: 'link-pointerdown',
            tools: [
                new InfoButton({ offset: 0, distance: distance_On_Link_To_Entry_Point })
            ]
        });

        linkView.addTools(toolsView);
    }

    initializeToolsAndInspector() {
        this.paper.on('cell:pointerup', (cellView, evt) => {
            this.selectMousePoint = { x: evt.clientX, y: evt.clientY }
            const cell = cellView.model;
            const { collection } = this.selection;
            collection.reset([cell]);
        });
    }

    simulate = async () => {
        this.hasUpdated(true)
        if (this.overallPopupOpen) {
            this.toggleOverallFinancialStreamPropertyPopup(this.graph, this.econVars, this.simulatorService.solved_Dictionary)
        }
        this.tempCloseAllPopups();
        this.simulatorService.simulate(this.graph, this.graph.getLinks(), this.graph.getElements(), this.isAuthenticated, this.getIdTokenClaims)
            .then(async () => {
                this.tempReopenAllPopups();
                toast.success("Simulation complete.")
                if (!this.simulatedOnce)
                    this.simulatedOnce = true;
                if (this.economicsSimulated)
                    this.economicsSimulated = false;
            })
            .catch(error => {
                console.error("Error during simulation: ", error);
                this.tempReopenAllPopups();
                //toast.error("Error during simulation.", error)
                //toast.error(`Simulation Error ${error.message || error}`);

                // Split error message by newline and format it for toast
                toast.error(
                    <div>
                        {error.message.split('\n').map((line, index) => (
                            <div key={index}>{line}</div>
                        ))}
                    </div>
                );





            });
    }

    initializeToolbar() {
        this.toolbarService.create(this.commandManager, this.paperScroller);

        this.toolbarService.toolbar.on('close-all:pointerclick', (event) => {
            if (this.overallPopupOpen) {
                this.toggleOverallFinancialStreamPropertyPopup(this.graph, this.econVars, this.simulatorService.solved_Dictionary)
            }
            this.closeAllPopups()
        })

        this.toolbarService.toolbar.on('print:pointerclick', (event) => {
            // Get text labels before printing
            const textLabels = this.textOverlayManager ? this.textOverlayManager.getLabelsForPrint() : [];

            this.paper.print({
                //padding: 10,
                sheet: { width: 297, height: 210 },
                poster: false,
                margin: 0,
                marginUnit: 'in',
                area: { width: 1587, height: 1123, x: 0, y: 0 },

                ready: (printAreaElements, readyToPrint) => {
                    printAreaElements.forEach(($el) => {
                        $el.find('#paper').css('border', 'none');
                        $el.find('.shapename').css('border', 'none');
                        $el.find('.ql-blank').css('display', 'none');

                        // Add text labels to the print area
                        const svg = $el.find('svg')[0];
                        if (svg && textLabels.length > 0) {
                            textLabels.forEach(label => {
                                // Create a foreignObject to hold the HTML text
                                const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
                                foreignObject.setAttribute('x', label.x - label.width / 2);
                                foreignObject.setAttribute('y', label.y);
                                foreignObject.setAttribute('width', label.width);
                                foreignObject.setAttribute('height', '100');

                                // Create the div to hold the text content
                                const div = document.createElement('div');
                                div.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
                                div.innerHTML = label.html;
                                div.style.cssText = 'font-family: Arial, sans-serif; font-size: 14px; text-align: center; color: black;';

                                foreignObject.appendChild(div);
                                svg.appendChild(foreignObject);
                            });
                        }
                    });
                    readyToPrint(printAreaElements);
                }
            });
        })

        this.toolbarService.toolbar.on('home:pointerclick', (event) => {
            if (this.isUpdated() && !this.isTemplate) {
                this.openConfirmPopup()
            } else {
                this.navigate("/")
            }
        })

        this.toolbarService.toolbar.on('toggle-simulate-load:pointerclick', (event) => {
            this.simulateOnLoad = !this.simulateOnLoad

            if (this.simulateOnLoad) {
                const button = document.getElementById('btn-toggle-simulate-load');

                button.style.backgroundColor = 'red';
            } else {
                const button = document.getElementById('btn-toggle-simulate-load');

                button.style.backgroundColor = '';
            }
        })

        this.toolbarService.toolbar.on('mb-switch:pointerclick', (event) => {
            // Blur any active element before simulating
            if (document.activeElement && document.activeElement.tagName === 'INPUT') {
                document.activeElement.blur();
            }
            this.simulate();
        });

        this.toolbarService.toolbar.on('save-as:pointerclick', (event) => {
            this.toggleSaveNamePopup()
        })

        this.toolbarService.toolbar.on('save-json:pointerclick', (event) => {
            this.saveGraph(this.graphGroup)
        })

        this.toolbarService.toolbar.on('switch-designer:pointerclick', (event) => {
            this.setGraphData(this.getGraphJSONSave(), this.graphName)
            this.useDesigner(this.uuid)
        })

        this.toolbarService.toolbar.on('streamtable:pointerclick', (event) => {
            streamTableExport(this.graph, this.graphName, this.econVars);
        })

        this.toolbarService.toolbar.on('economics:pointerclick', async (event) => {
            if (!this.simulatedOnce) {
                toast.error("Please simulate before viewing economics.")
                return;
            }

            let specify_All = false
            for (let link in this.graph.getLinks()) {
                for (let mF in this.graph.getLinks()[link].attributes.specified_Stream_Properties.massFrac) {
                    if (this.graph.getLinks()[link].attributes.specified_Stream_Properties.massFrac[mF] != null) {
                        specify_All = true
                    }
                }

                for (let mF in this.graph.getLinks()[link].attributes.specified_Stream_Properties.massFlow) {
                    if (this.graph.getLinks()[link].attributes.specified_Stream_Properties.massFlow[mF] != null) {
                        specify_All = true
                    }
                }

                if (this.graph.getLinks()[link].attributes.specified_Stream_Properties.totalFlow != null) {
                    specify_All = true
                }
            }

            if (!specify_All) {
                toast.error("Please specify all mass before viewing economics.")
                return;
            }

            if (this.overallPopupOpen) {
                this.toggleOverallFinancialStreamPropertyPopup(this.graph, this.econVars, this.simulatorService.solved_Dictionary)
            }

            try {
                await this.simulateEconVars(this.graph);
                if (!this.economicsSimulated)
                    this.economicsSimulated = true;
            } catch (error) {
                toast.error(error.message);
                return;
            }

            this.toggleOverallFinancialStreamPropertyPopup(this.graph, this.econVars, this.simulatorService.solved_Dictionary)
        })
        
        this.toolbarService.toolbar.on('upload:pointerclick', (event) => {
            var el = window._protected_reference = document.createElement("INPUT");
            el.type = "file";
            el.accept = "application/json";

            el.addEventListener('change', (ev2) => {
                var file = ev2.target.files[0];  // Get the selected file

                if (file) {
                    var reader = new FileReader();
                    reader.onload = (e) => {
                        try {
                            if (this.overallPopupOpen) {
                                this.toggleOverallFinancialStreamPropertyPopup(this.graph, this.econVars, this.simulatorService.solved_Dictionary)
                            }
                            this.simulatedOnce = false;

                            if (this.graph.getCells().length > 0) {
                                this.graph.clear();
                            }

                            this.resetEconVars()

                            var json = JSON.parse(e.target.result);
                            var econVars = this.econVars

                            for (var key in json) {
                                if (key !== 'cells') {
                                    if (key == 'mainFeed') {
                                    } else {
                                        for (let key in econVars) {
                                            if (json.hasOwnProperty(key)) {
                                                econVars[key] = json[key];
                                            }
                                        }
                                    }
                                }
                            }

                            this.graph.fromJSON(json);

                            var graph = this.graph

                            let all_Shapes = graph.getElements();
                            let all_Streams = graph.getLinks();
                            for (let i = 0; i < all_Streams.length; i++) {
                                if (!(all_Streams[i].attributes['specified_Stream_Properties'])) {
                                    // STREAM PROP DONT EXIST
                                    all_Streams[i].attributes["stream_Number"] = String(all_Streams[i].attributes["CustomName"])
                                    // CREATE OBJECTS
                                    all_Streams[i].attributes['specified_Stream_Properties'] = { "massFlow": {}, "massFrac": {}, "totalFlow": null }
                                    all_Streams[i].attributes['complete_Stream_Properties'] = { "massFlow": {}, "massFrac": {}, "totalFlow": null }

                                    let component_Objects = all_Streams[i].attributes['components']
                                    let listed_Component_Objects = Object.keys(component_Objects)
                                    for (let j = 0; j < listed_Component_Objects.length; j++) {
                                        all_Streams[i].attributes['specified_Stream_Properties']['massFlow'][listed_Component_Objects[j]] = null
                                        all_Streams[i].attributes['specified_Stream_Properties']['massFrac'][listed_Component_Objects[j]] = null
                                        all_Streams[i].attributes['complete_Stream_Properties']['massFlow'][listed_Component_Objects[j]] = null
                                        all_Streams[i].attributes['complete_Stream_Properties']['massFrac'][listed_Component_Objects[j]] = null
                                    }



                                }
                            }

                            for (let i = 0; i < all_Shapes.length; i++) {
                                if (all_Shapes[i].prop('isUnit') == 'yes') {
                                    // IF IT DOESN'T ALREADY EXIST
                                    if (!(all_Shapes[i].attributes['equipment_Properties'])) {

                                        // INPUT AND OUTPUT STREAM LISTS ARE NEEDED FOR SOLVER
                                        // WILL ALSO NEED TO EXTRACT COMPONENTS FROM THESE LISTS
                                        var unit_Components = []
                                        all_Shapes[i].attributes['equipment_Properties'] = {}
                                        all_Shapes[i].attributes['equipment_Properties']['unitType'] = all_Shapes[i].attributes['unitType']
                                        var outbound_Links = graph.getConnectedLinks(all_Shapes[i], { outbound: true });
                                        var outbound_Links_List = []
                                        for (let j = 0; j < outbound_Links.length; j++) {
                                            outbound_Links_List.push(String(outbound_Links[j].prop('CustomName')))


                                        }
                                        all_Shapes[i].attributes['equipment_Properties']['outputStreams'] = outbound_Links_List

                                        var inbound_Links = graph.getConnectedLinks(all_Shapes[i], { inbound: true });
                                        var inbound_Links_List = []
                                        for (let j = 0; j < inbound_Links.length; j++) {
                                            inbound_Links_List.push(String(inbound_Links[j].prop('CustomName')))

                                        }
                                        all_Shapes[i].attributes['equipment_Properties']['inputStreams'] = inbound_Links_List

                                        // Just use the components list directly
                                        let unit_Component_Objects = all_Shapes[i].attributes['components'] || []
                                        let listed_Unit_Components = Object.keys(unit_Component_Objects)
                                        all_Shapes[i].attributes['equipment_Properties']['components'] = listed_Unit_Components


                                    }
                                }

                                all_Shapes[i].attributes['unit_Label'] = all_Shapes[i].attributes['id']
                            }

                            if (this.overallPopupOpen) {
                                this.toggleOverallFinancialStreamPropertyPopup(this.graph, this.econVars, this.simulatorService.solved_Dictionary)
                            }
                            this.closeAllPopups()
                            if (this.simulateOnLoad) {
                                this.simulate()
                            }

                            if (this.reactions_Left(all_Shapes)) {
                                this.spec_Reactions(this.reactions_Left(all_Shapes)[1])
                            }
                        } catch (error) {
                            console.error("Error parsing JSON:", error);
                        }
                    };

                    reader.readAsText(file);  // Initiates the reading of the file's content.
                }
            });


            el.click()

        });

        this.renderPlugin('.toolbar-container', this.toolbarService.toolbar);
    }


    loadGraph = (graphName, uuid, json, updated) => {

    
        this.graphName = graphName
        this.uuid = uuid
        this.graph.fromJSON(json);
    
        var graph = this.graph
        let all_Shapes = graph.getElements();
        let all_Streams = graph.getLinks();
    
        // If econVars are present in the saved JSON, use them.
        if (json.econVars) {
            this.econVars = json.econVars;
        } else {
            // Otherwise, initialize econVars by resetting (which creates the default structure)
            this.resetEconVars();
        }
    
        for (let i = 0; i < all_Streams.length; i++) {
            if (!(all_Streams[i].attributes['specified_Stream_Properties'])) {
                // STREAM PROP DONT EXIST
                all_Streams[i].attributes["stream_Number"] = String(all_Streams[i].attributes["CustomName"])
                // CREATE OBJECTS
                all_Streams[i].attributes['specified_Stream_Properties'] = { "massFlow": {}, "massFrac": {}, "totalFlow": null }
                all_Streams[i].attributes['complete_Stream_Properties'] = { "massFlow": {}, "massFrac": {}, "totalFlow": null }
            
                let component_Objects = all_Streams[i].attributes['components'] || []
                let listed_Component_Objects = Object.keys(component_Objects)
                for (let j = 0; j < listed_Component_Objects.length; j++) {
                    all_Streams[i].attributes['specified_Stream_Properties']['massFlow'][listed_Component_Objects[j]] = null
                    all_Streams[i].attributes['specified_Stream_Properties']['massFrac'][listed_Component_Objects[j]] = null
                    all_Streams[i].attributes['complete_Stream_Properties']['massFlow'][listed_Component_Objects[j]] = null
                    all_Streams[i].attributes['complete_Stream_Properties']['massFrac'][listed_Component_Objects[j]] = null
                }
            }
        }
    
        //Creating equipment properties components needed for backend
        //needs to be edited --> for matrix updated GTB
        for (let i = 0; i < all_Shapes.length; i++) {
            if (all_Shapes[i].prop('isUnit') == 'yes') {
                var element = all_Shapes[i]
                var globalObjectsList = this.econVars
                let unit = graph.getCell(element)
                let totalFlow = 0
                let skipOpexCalculation = false;
                //check if the unit is a feed or product, then it doesn't need a duty
                try {
                    if (unit.attributes['equipment_Properties']['unitType'] != 'feed' || unit.attributes['equipment_Properties']['unitType'] != 'Feed' || unit.attributes['equipment_Properties']['unitType'] != 'product' || unit.attributes['equipment_Properties']['unitType'] != 'Product') {
                        //utputStreams = graph.getConnectedLinks(element)
                        //let totalFlow = parseFloat(graph.getConnectedLinks(unit)[0].attributes['complete_Stream_Properties']['totalFlow'])
                        let streamList = graph.getLinks(element)
                        
                        // if(!unit.attributes['equipment_Economics']['Duty']) unit.attributes['equipment_Economics']['Duty'] = {}
                        let duty = unit.attributes['equipment_Economics']['Duty']['Value']
                        let outputStreams = unit.attributes['equipment_Properties']['outputStreams']
                        //write a for loop for calculting the total flow based on the flow of each outputStream
                        for (let i = 0; i < outputStreams.length; i++) {
                            let result = streamList.find(item => item.attributes && item.attributes.CustomName === parseInt(outputStreams[i]));
                            let specificFlow = result.attributes['complete_Stream_Properties']['totalFlow']
                            //add the value of total flow to a variable
                            totalFlow += specificFlow
                        }
                        let totalDuty = duty //removing scaling
                        //reads the duty from the elements duty
                        //retreive the electricity cost from the global objects list
    
                        // ADD THIS CHECK - Skip calculation if OPEX already exists and has a value
                        if (unit.attributes['equipment_Economics']['Opex'] && 
                            unit.attributes['equipment_Economics']['Opex']['Value'] > 0) {
                            // OPEX already exists, don't overwrite it
                            skipOpexCalculation = true;
                        }

                        if (!skipOpexCalculation) {
    
                            let electricityCost = globalObjectsList.elecCost
                            let opexUnit = totalDuty * (electricityCost / 1000) * 8000
                            //save the opexUnit into the opex div
                            unit.attributes['equipment_Economics']['Opex']['Value'] = opexUnit
                        }
                    }
                } catch (e) {
                }
                //creates equipment_Properties
                //GTB edit when dismantling
                // IF IT DOESN'T ALREADY EXIST
                if (!(all_Shapes[i].attributes['equipment_Properties'])) {
                    // Only initialize if it doesn't exist
                    all_Shapes[i].attributes['equipment_Properties'] = {}
                    all_Shapes[i].attributes['equipment_Properties']['unitType'] = all_Shapes[i].attributes['unitType']
                }
    
                // Do these operations regardless of whether equipment_Properties existed
                var outbound_Links = graph.getConnectedLinks(all_Shapes[i], { outbound: true });
                var outbound_Links_List = outbound_Links.map(link => String(link.prop('CustomName')))
                all_Shapes[i].attributes['equipment_Properties']['outputStreams'] = outbound_Links_List
    
                var inbound_Links = graph.getConnectedLinks(all_Shapes[i], { inbound: true });
                var inbound_Links_List = inbound_Links.map(link => String(link.prop('CustomName')))
                all_Shapes[i].attributes['equipment_Properties']['inputStreams'] = inbound_Links_List
    
                let unit_Component_Objects = all_Shapes[i].attributes['components'] || []
                let listed_Unit_Components = Object.keys(unit_Component_Objects)
                all_Shapes[i].attributes['equipment_Properties']['components'] = listed_Unit_Components
            }
    
            // DISABLED: Old foreignObject Quill initialization - now using TextOverlayManager
            // if (all_Shapes[i].attr('shapename/id')) {
            //     this.initializeQuillWhenReady(all_Shapes[i]);
            // }
    
            all_Shapes[i].attributes['unit_Label'] = all_Shapes[i].attributes['id']
    
        }
    
        const links = this.graph.getLinks()
    
        let lowest_Stream_Number = Infinity;
    
        for (const link of links) {
            const targetCell = this.graph.getCell(link.get('target').id);
            if (targetCell == undefined) continue;
            if (targetCell.attributes.unitType == undefined) continue;
            if (targetCell.attributes.unitType == "Product") {
                if (Number(link.attributes.stream_Number) < lowest_Stream_Number) {
                    lowest_Stream_Number = Number(link.attributes.stream_Number)
                }
            }
        }
    
        // Only auto-update if the current value matches the calculated lowest
        if (this.econVars.keyProductStream === lowest_Stream_Number) {
            this.econVars.keyProductStream = lowest_Stream_Number;
        }
    
        this.closeAllPopups()
        if (this.simulateOnLoad) {
            this.simulate()
        }
    
        this.hasUpdated(updated)

        all_Shapes.forEach(shape => {
        if (shape.attr('shapename/id')) {
            const elementId = shape.attr('shapename/id');
            const element = document.getElementById(elementId);
            }
        });
    
        // Check for any missed elements after everything is loaded
        // More aggressive checking for missed elements
        const checkForMissedElements = () => {
        let foundMissed = false;
        
        all_Shapes.forEach(shape => {
            if (shape.attr('shapename/id')) {
                const elementId = shape.attr('shapename/id');
                const element = document.getElementById(elementId);
                
                if (element) {
                    const hasQuillEditor = !!element.querySelector('.ql-editor');
                    const isContentEditable = element.hasAttribute('contenteditable') || element.hasAttribute('content-editable');
                    const hasInnerHtml = element.hasAttribute('inner-h-t-m-l');
                    
                    // Check if it's broken (no Quill structure but should have one)
                    if (!hasQuillEditor && (isContentEditable || hasInnerHtml || element.innerHTML.includes('<p>'))) {
                        
                        
                        foundMissed = true;
                        
                        // Manually initialize this missed element
                        try {
                            
                            // Get saved content
                            const savedContent = shape.attr('shapename/innerHTML') || 
                                            element.getAttribute('inner-h-t-m-l') || 
                                            element.innerHTML || 
                                            '<p><br></p>';
                            
                            // Clear element
                            element.innerHTML = '';
                            
                            // Initialize Quill
                            const quill = new Quill(element, {
                                modules: {},
                                theme: 'bubble',
                                bounds: document.getElementById(shape.id + 'QuillParentDiv')
                            });
                            
                            // Set content
                            if (savedContent && savedContent !== '<p><br></p>') {
                                quill.clipboard.dangerouslyPasteHTML(savedContent);
                            }
                            
                            // Apply styles
                            this.applyStyles(shape);
                            
                            // Add process-shape class
                            const editor = element.querySelector('.ql-editor');
                            if (editor) {
                                editor.classList.add("process-shape");
                            }
                            
                            // Ensure parent div is visible
                            const parentDiv = document.getElementById(shape.id + 'QuillParentDiv');
                            if (parentDiv) {
                                parentDiv.style.display = shape.attr('foreignObject/style/display') || 'block';
                            }
                            
                        } catch (error) {
                            console.error('Failed to fix missed element:', elementId, error);
                        }
                    }
                }
            }
        });

    };

    // DISABLED: Old foreignObject Quill missed elements check - now using TextOverlayManager
    // setTimeout(checkForMissedElements, 100);
    // setTimeout(checkForMissedElements, 500);
    // setTimeout(checkForMissedElements, 1000);
    // setTimeout(checkForMissedElements, 2000);

    // Initialize text overlay editors for existing shapes
    if (this.textOverlayManager) {
        // Clear any existing editors first (in case of graph reload)
        this.textOverlayManager.clearAllEditors();
        // Delay to ensure DOM and paper scroller are fully positioned
        setTimeout(() => {
            this.textOverlayManager.initializeExistingShapes();
            // Update positions again after another short delay to ensure accuracy
            setTimeout(() => {
                this.textOverlayManager.updateAllPositions();
            }, 100);
        }, 200);
    }
    }

    saveGraph = async (graphGroup) => {
    

        if (this.graphName == null || this.graphName == "") {
            this.toggleSaveNamePopup()
            return true;
        }

        if (this.hitSaveOnce) return false;
        if (this.hitSaveOnce && this.uuid == null) return false;
        if (!this.hitSaveOnce) this.hitSaveOnce = true
        if (this.uuid == "new") this.uuid = null
        // Only update this.graphGroup if graphGroup is provided
        if (graphGroup !== undefined && graphGroup !== null) {
            this.graphGroup = graphGroup;
        }
        var graphData = this.getGraphJSONSave()
        //console.log(JSON.stringify(graphData))

        await FlowireAPI.authPost('/api/save', graphData, this.getIdTokenClaims, this.isAuthenticated, (error) => {
            console.error("Error saving graph: ", error);
            if (error.status == 405) {
                toast.error("Error saving graph: No permission to save this graph.")
            } else {
                toast.error("Error saving graph: " + error.statusText)
            }
        }).then((ans) => {
            if (ans.uuid) {
                this.uuid = ans.uuid
                toast.success("Successfully Saved Simulation!")
                this.hasUpdated(true)
            }
        })

        if (this.hitSaveOnce) this.hitSaveOnce = false
        return false
    }

    getGraphJSONSave() {
        const graphData = this.graph.toJSON();
        
        // If econVars have been created (by simulator mode) then include them
        if (this.econVars) {
          graphData.econVars = this.econVars;
        }
        
        Object.assign(graphData, {
          uuid: this.uuid,
          graphName: this.graphName,
          designer: false,
          group_uuid: this.graphGroup
        });
        
        return graphData;
      }
      
    

    initializeKeyboardShortcuts() {

        this.keyboardService.create(this.graph, this.clipboard, this.selection, this.paperScroller, this.commandManager);
    }

    renderPlugin(selector, plugin) {
        this.el.querySelector(selector).appendChild(plugin.el);
        plugin.render();
    }
}

export default KitchenSinkService;
